import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PaymentMethod } from "../types";
import { formatCurrencyBDT } from "./currency";
import { PAYMENT_METHOD_LABELS } from "./paymentMethods";
import { CompanyInfo, DEFAULT_COMPANY_INFO } from "./pdfGenerator";

// House colors, matching PurchaseOrderPDFGenerator so every PDF this app
// produces shares one identity.
const HEADER_FILL: [number, number, number] = [6, 53, 62];
const ZEBRA_FILL: [number, number, number] = [245, 245, 245];
const SUCCESS_COLOR: [number, number, number] = [46, 125, 50];
const ERROR_COLOR: [number, number, number] = [198, 40, 40];
const WARNING_COLOR: [number, number, number] = [180, 95, 6];
const MUTED_COLOR: [number, number, number] = [110, 110, 110];

export type MediumTotalsForPdf = Partial<Record<PaymentMethod, number>>;

export interface CashFlowReportSubsection {
    count: number;
    byMedium: MediumTotalsForPdf;
    subtotal: number;
}

export interface CashFlowReportData {
    // Pre-formatted by the caller (DailyCashFlowAnalyzer already owns the
    // local-datetime parsing/formatting rules) — this generator only lays
    // out already-computed values, it never derives new figures.
    dateRangeLabel: string;
    kpis: {
        totalIncome: number;
        totalSpending: number;
        netCashChange: number;
        pendingCodRemittance: number;
    };
    income: {
        pos: CashFlowReportSubsection & { untaggedCount: number };
        inHouse: CashFlowReportSubsection & {
            byCourier: Record<string, number>;
        };
        totalIncome: number;
        thirdPartyDirectTotal: number;
    };
    spending: {
        expenses: CashFlowReportSubsection;
        procurement: CashFlowReportSubsection;
        assets: CashFlowReportSubsection;
        totalSpending: number;
    };
    cashReconciliation: {
        cashIn: number;
        cashOut: number;
        netCashChange: number;
        totalIncomeAllMediums: number;
        totalSpendingAllMediums: number;
        netAllMediums: number;
    };
    thirdParty: {
        orderCount: number;
        byCourier: Record<string, number>;
        directByMedium: MediumTotalsForPdf;
        directTotal: number;
        codGross: number;
        codDeliveryCost: number;
        codFeeRatePct: number;
        codFee: number;
        codNetExpected: number;
    };
    allMediumsSummary: Array<{
        method: PaymentMethod;
        received: number;
        spent: number;
        net: number;
    }>;
}

// "Tk " rather than "৳" — jsPDF's built-in fonts (Helvetica) don't carry the
// Bengali currency glyph, so printing it renders as a missing-glyph box (or
// a substitute character, depending on the font's cmap). formatCurrencyBDT
// already prepends "৳ " itself, so that's stripped off and replaced with
// the ASCII-safe prefix rather than stacking both.
const money = (amount: number): string =>
    `Tk ${formatCurrencyBDT(amount).replace(/^৳\s*/, "")}`;

export class CashFlowReportPDFGenerator {
    private doc: jsPDF;
    private readonly pageWidth: number;
    private readonly pageHeight: number;
    private readonly margin: number = 15;
    private yPosition: number = 15;
    private companyInfo: CompanyInfo;

    constructor(companyInfo?: CompanyInfo) {
        this.doc = new jsPDF({
            orientation: "portrait",
            unit: "mm",
            format: "a4",
        });
        this.pageWidth = this.doc.internal.pageSize.getWidth();
        this.pageHeight = this.doc.internal.pageSize.getHeight();
        this.companyInfo = companyInfo || DEFAULT_COMPANY_INFO;
    }

    private async loadLogo(): Promise<string | null> {
        try {
            const logoUrl = this.companyInfo.logoUrl || "/images/logo192.png";
            const response = await fetch(logoUrl);
            if (!response.ok) return null;
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("Failed to load logo:", error);
            return null;
        }
    }

    // Starts a fresh page if the next block wouldn't fit above the footer.
    private ensureSpace(neededMm: number) {
        if (this.yPosition + neededMm > this.pageHeight - 18) {
            this.doc.addPage();
            this.yPosition = this.margin;
        }
    }

    private addHeader(logoData: string | null) {
        const headerStartY = this.yPosition;

        if (logoData) {
            try {
                this.doc.addImage(
                    logoData,
                    "PNG",
                    this.margin,
                    headerStartY,
                    25,
                    25,
                );
            } catch (error) {
                console.error("Failed to add logo to PDF:", error);
            }
        }

        const textStartX = this.margin + (logoData ? 30 : 0);
        this.doc.setFontSize(20);
        this.doc.setFont("helvetica", "bold");
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(this.companyInfo.name, textStartX, headerStartY + 8);

        this.doc.setFontSize(10);
        this.doc.setFont("helvetica", "normal");

        let contactY = headerStartY + 14;
        if (this.companyInfo.phone) {
            this.doc.text(
                `Phone: ${this.companyInfo.phone}`,
                textStartX,
                contactY,
            );
            contactY += 5;
        }
        if (this.companyInfo.address) {
            this.doc.text(this.companyInfo.address, textStartX, contactY);
            contactY += 5;
        }

        this.yPosition = Math.max(headerStartY + 30, contactY + 5);
    }

    private addReportTitle(dateRangeLabel: string) {
        this.doc.setFontSize(15);
        this.doc.setFont("helvetica", "bold");
        this.doc.setTextColor(0, 0, 0);
        this.doc.text("Cash Flow Report", this.margin, this.yPosition);
        this.yPosition += 6;

        this.doc.setFontSize(10);
        this.doc.setFont("helvetica", "normal");
        this.doc.setTextColor(...MUTED_COLOR);
        this.doc.text(`Period: ${dateRangeLabel}`, this.margin, this.yPosition);
        this.doc.setTextColor(0, 0, 0);
        this.yPosition += 9;
    }

    // The four headline figures, as one compact row — mirrors the web
    // page's stat-tile row without inventing any new numbers.
    private addKpiSummary(kpis: CashFlowReportData["kpis"]) {
        this.ensureSpace(24);

        const cells: Array<{
            label: string;
            value: number;
            color: [number, number, number];
        }> = [
            {
                label: "Total Income",
                value: kpis.totalIncome,
                color: SUCCESS_COLOR,
            },
            {
                label: "Total Spending",
                value: kpis.totalSpending,
                color: ERROR_COLOR,
            },
            {
                label: "Net Cash Change",
                value: kpis.netCashChange,
                color: kpis.netCashChange >= 0 ? SUCCESS_COLOR : ERROR_COLOR,
            },
            {
                label: "Pending COD Remittance",
                value: kpis.pendingCodRemittance,
                color: WARNING_COLOR,
            },
        ];

        const usableWidth = this.pageWidth - this.margin * 2;
        const boxWidth = usableWidth / cells.length;
        const boxHeight = 20;
        const boxY = this.yPosition;

        cells.forEach((cell, index) => {
            const boxX = this.margin + index * boxWidth;
            this.doc.setDrawColor(215, 215, 215);
            this.doc.setLineWidth(0.2);
            this.doc.rect(boxX, boxY, boxWidth - 2, boxHeight);

            this.doc.setFontSize(7.5);
            this.doc.setFont("helvetica", "normal");
            this.doc.setTextColor(...MUTED_COLOR);
            const labelLines = this.doc.splitTextToSize(
                cell.label,
                boxWidth - 6,
            );
            this.doc.text(labelLines, boxX + 3, boxY + 5);

            this.doc.setFontSize(10.5);
            this.doc.setFont("helvetica", "bold");
            this.doc.setTextColor(...cell.color);
            this.doc.text(money(cell.value), boxX + 3, boxY + 15);
        });

        this.doc.setTextColor(0, 0, 0);
        this.yPosition = boxY + boxHeight + 8;
    }

    private addSectionHeading(title: string) {
        this.ensureSpace(12);
        this.doc.setFillColor(...HEADER_FILL);
        this.doc.rect(this.margin, this.yPosition - 5, this.pageWidth - this.margin * 2, 8, "F");
        this.doc.setFontSize(11);
        this.doc.setFont("helvetica", "bold");
        this.doc.setTextColor(255, 255, 255);
        this.doc.text(title, this.margin + 3, this.yPosition + 0.5);
        this.doc.setTextColor(0, 0, 0);
        this.yPosition += 9;
    }

    private addSubsectionLabel(label: string) {
        this.ensureSpace(8);
        this.doc.setFontSize(10);
        this.doc.setFont("helvetica", "bold");
        this.doc.text(label, this.margin, this.yPosition);
        this.yPosition += 5;
    }

    private addNote(text: string, color: [number, number, number]) {
        this.ensureSpace(10);
        this.doc.setFontSize(8.5);
        this.doc.setFont("helvetica", "italic");
        this.doc.setTextColor(...color);
        const lines = this.doc.splitTextToSize(
            text,
            this.pageWidth - this.margin * 2,
        );
        this.doc.text(lines, this.margin, this.yPosition);
        this.doc.setTextColor(0, 0, 0);
        this.yPosition += lines.length * 4 + 3;
    }

    // Method / Amount table shared by every payment-medium breakdown on the
    // page (POS, in-house, expenses, procurement, assets, third-party
    // direct) — one implementation instead of six near-identical copies.
    private addMediumTable(totals: MediumTotalsForPdf) {
        const entries = Object.entries(totals) as [PaymentMethod, number][];
        if (entries.length === 0) {
            this.addNote("No payments recorded.", MUTED_COLOR);
            return;
        }
        entries.sort((a, b) => b[1] - a[1]);

        this.ensureSpace(14);
        autoTable(this.doc, {
            startY: this.yPosition,
            head: [["Method", "Amount"]],
            body: entries.map(([method, amount]) => [
                PAYMENT_METHOD_LABELS[method],
                money(amount),
            ]),
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2.5 },
            headStyles: {
                fillColor: HEADER_FILL,
                textColor: 255,
                fontStyle: "bold",
            },
            columnStyles: { 1: { halign: "right" } },
            alternateRowStyles: { fillColor: ZEBRA_FILL },
            margin: { left: this.margin, right: this.margin },
        });
        this.yPosition = (this.doc as any).lastAutoTable.finalY + 5;
    }

    private addCourierTable(byCourier: Record<string, number>) {
        const entries = Object.entries(byCourier);
        if (entries.length === 0) return;
        entries.sort((a, b) => b[1] - a[1]);

        this.ensureSpace(14);
        autoTable(this.doc, {
            startY: this.yPosition,
            head: [["Courier", "Collected"]],
            body: entries.map(([courier, amount]) => [courier, money(amount)]),
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2.5 },
            headStyles: {
                fillColor: HEADER_FILL,
                textColor: 255,
                fontStyle: "bold",
            },
            columnStyles: { 1: { halign: "right" } },
            alternateRowStyles: { fillColor: ZEBRA_FILL },
            margin: { left: this.margin, right: this.margin },
        });
        this.yPosition = (this.doc as any).lastAutoTable.finalY + 5;
    }

    private addAmountLine(
        label: string,
        amount: number,
        options?: { bold?: boolean; color?: [number, number, number] },
    ) {
        this.ensureSpace(7);
        const bold = options?.bold ?? false;
        this.doc.setFontSize(bold ? 11 : 9.5);
        this.doc.setFont("helvetica", bold ? "bold" : "normal");
        this.doc.setTextColor(0, 0, 0);
        this.doc.text(label, this.margin, this.yPosition);
        if (options?.color) this.doc.setTextColor(...options.color);
        this.doc.text(
            money(amount),
            this.pageWidth - this.margin,
            this.yPosition,
            { align: "right" },
        );
        this.doc.setTextColor(0, 0, 0);
        this.yPosition += bold ? 7 : 6;
    }

    private addIncomeSection(income: CashFlowReportData["income"]) {
        this.addSectionHeading("Income");

        this.addSubsectionLabel(
            `Offline (POS) Sales — ${income.pos.count} order${
                income.pos.count !== 1 ? "s" : ""
            }`,
        );
        if (income.pos.untaggedCount > 0) {
            this.addNote(
                `${income.pos.untaggedCount} of these orders were created without an Order Channel set, so counted here as Offline by default.`,
                WARNING_COLOR,
            );
        }
        this.addMediumTable(income.pos.byMedium);
        this.addAmountLine("Subtotal", income.pos.subtotal);

        this.addSubsectionLabel(
            `In-house Delivery Collections — ${income.inHouse.count} order${
                income.inHouse.count !== 1 ? "s" : ""
            }`,
        );
        this.addCourierTable(income.inHouse.byCourier);
        this.addMediumTable(income.inHouse.byMedium);
        this.addAmountLine("Subtotal", income.inHouse.subtotal);

        this.addAmountLine("Total Income", income.totalIncome, {
            bold: true,
            color: SUCCESS_COLOR,
        });
        if (income.thirdPartyDirectTotal > 0) {
            this.addNote(
                `Including ${money(income.thirdPartyDirectTotal)} paid directly to us via courier deliveries (see Third-Party Courier Collections below).`,
                MUTED_COLOR,
            );
        }
        this.yPosition += 3;
    }

    private addSpendingSection(spending: CashFlowReportData["spending"]) {
        this.addSectionHeading("Spending");

        this.addSubsectionLabel(
            `Expenses — ${spending.expenses.count} entr${
                spending.expenses.count !== 1 ? "ies" : "y"
            }`,
        );
        this.addMediumTable(spending.expenses.byMedium);
        this.addAmountLine("Subtotal", spending.expenses.subtotal);

        this.addSubsectionLabel(
            `Procurement Payments — ${spending.procurement.count} payment${
                spending.procurement.count !== 1 ? "s" : ""
            }`,
        );
        this.addMediumTable(spending.procurement.byMedium);
        this.addAmountLine("Subtotal", spending.procurement.subtotal);

        this.addSubsectionLabel(
            `Asset Purchases — ${spending.assets.count} item${
                spending.assets.count !== 1 ? "s" : ""
            }`,
        );
        this.addMediumTable(spending.assets.byMedium);
        this.addAmountLine("Subtotal", spending.assets.subtotal);

        this.addAmountLine("Total Spending", spending.totalSpending, {
            bold: true,
            color: ERROR_COLOR,
        });
        this.yPosition += 3;
    }

    private addCashReconciliationSection(
        recon: CashFlowReportData["cashReconciliation"],
    ) {
        this.addSectionHeading("Cash Reconciliation");
        this.addNote(
            "Only Cash and in-house-collected COD (money physically collected in hand) feed into this. Third-party courier COD is tracked separately below.",
            MUTED_COLOR,
        );
        this.addAmountLine("Cash & COD In", recon.cashIn, {
            color: SUCCESS_COLOR,
        });
        this.addAmountLine("Cash & COD Out", recon.cashOut, {
            color: ERROR_COLOR,
        });
        this.addAmountLine("Net Cash Change", recon.netCashChange, {
            bold: true,
            color: recon.netCashChange >= 0 ? SUCCESS_COLOR : ERROR_COLOR,
        });
        this.yPosition += 2;
        this.addAmountLine(
            "Total Income (all mediums)",
            recon.totalIncomeAllMediums,
        );
        this.addAmountLine(
            "Total Spending (all mediums)",
            recon.totalSpendingAllMediums,
        );
        this.addAmountLine("Net (all mediums)", recon.netAllMediums, {
            color: recon.netAllMediums >= 0 ? SUCCESS_COLOR : ERROR_COLOR,
        });
        this.yPosition += 3;
    }

    private addThirdPartySection(tp: CashFlowReportData["thirdParty"]) {
        this.addSectionHeading("Third-Party Courier Collections");
        this.addSubsectionLabel(
            `${tp.orderCount} order${tp.orderCount !== 1 ? "s" : ""}`,
        );
        this.addCourierTable(tp.byCourier);

        if (tp.directTotal > 0) {
            this.addNote(
                "Paid directly to us (already included in Total Income above):",
                MUTED_COLOR,
            );
            this.addMediumTable(tp.directByMedium);
        }

        this.yPosition += 1;
        this.addNote(
            "Collected via COD (pending remittance — not counted in Total Income):",
            MUTED_COLOR,
        );
        this.addAmountLine("Gross COD Collected", tp.codGross);
        this.addAmountLine("Delivery Cost", tp.codDeliveryCost, {
            color: ERROR_COLOR,
        });
        this.addAmountLine(
            `Est. Courier Fee (${tp.codFeeRatePct}% of remainder)`,
            tp.codFee,
            { color: ERROR_COLOR },
        );
        this.addAmountLine("Expected Net Remittance", tp.codNetExpected, {
            bold: true,
            color: WARNING_COLOR,
        });

        if (tp.codGross > 0) {
            this.addNote(
                "This isn't cash in hand yet — confirm the expected net remittance shows up in the courier's account once they settle it.",
                WARNING_COLOR,
            );
        }
        this.yPosition += 3;
    }

    private addAllMediumsSummary(
        rows: CashFlowReportData["allMediumsSummary"],
        totals: { received: number; spent: number; net: number },
    ) {
        this.addSectionHeading("All Payment Mediums — Summary");

        if (rows.length === 0) {
            this.addNote("No activity recorded for this date range.", MUTED_COLOR);
            return;
        }

        this.ensureSpace(14);
        const body = rows.map((row) => [
            PAYMENT_METHOD_LABELS[row.method],
            money(row.received),
            money(row.spent),
            money(row.net),
        ]);
        body.push([
            "Total",
            money(totals.received),
            money(totals.spent),
            money(totals.net),
        ]);

        autoTable(this.doc, {
            startY: this.yPosition,
            head: [["Medium", "Received", "Spent", "Net"]],
            body,
            theme: "grid",
            styles: { fontSize: 9, cellPadding: 2.5 },
            headStyles: {
                fillColor: HEADER_FILL,
                textColor: 255,
                fontStyle: "bold",
            },
            columnStyles: {
                1: { halign: "right" },
                2: { halign: "right" },
                3: { halign: "right" },
            },
            alternateRowStyles: { fillColor: ZEBRA_FILL },
            margin: { left: this.margin, right: this.margin },
            didParseCell: (hookData) => {
                if (hookData.row.index === body.length - 1) {
                    hookData.cell.styles.fontStyle = "bold";
                }
            },
        });
        this.yPosition = (this.doc as any).lastAutoTable.finalY + 5;
    }

    // Applied after every page exists, since "Page X of Y" needs the final
    // page count up front.
    private addFooterOnAllPages() {
        const pageCount = this.doc.getNumberOfPages();
        const generatedOn = `Generated on ${new Date().toLocaleString()}`;
        for (let i = 1; i <= pageCount; i++) {
            this.doc.setPage(i);
            const footerY = this.pageHeight - 10;
            this.doc.setFontSize(8);
            this.doc.setFont("helvetica", "italic");
            this.doc.setTextColor(128, 128, 128);
            this.doc.text(generatedOn, this.margin, footerY);
            this.doc.text(
                `Page ${i} of ${pageCount}`,
                this.pageWidth - this.margin,
                footerY,
                { align: "right" },
            );
            this.doc.setTextColor(0, 0, 0);
        }
    }

    async generate(data: CashFlowReportData): Promise<jsPDF> {
        const logoData = await this.loadLogo();

        this.addHeader(logoData);
        this.addReportTitle(data.dateRangeLabel);
        this.addKpiSummary(data.kpis);
        this.addIncomeSection(data.income);
        this.addSpendingSection(data.spending);
        this.addCashReconciliationSection(data.cashReconciliation);
        this.addThirdPartySection(data.thirdParty);
        this.addAllMediumsSummary(data.allMediumsSummary, {
            received: data.cashReconciliation.totalIncomeAllMediums,
            spent: data.cashReconciliation.totalSpendingAllMediums,
            net: data.cashReconciliation.netAllMediums,
        });
        this.addFooterOnAllPages();

        return this.doc;
    }

    async download(data: CashFlowReportData, filename?: string): Promise<void> {
        await this.generate(data);
        this.doc.save(filename || "Cash-Flow-Report.pdf");
    }
}
